import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  InputSignal,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SzIconComponent } from '../../display/sz-icon';

export type SzPasswordAutocomplete = 'new-password' | 'current-password' | 'off';

@Component({
  selector: 'sz-password-input',
  templateUrl: './sz-password-input.component.html',
  styleUrls: ['./sz-password-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SzIconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SzPasswordInputComponent),
      multi: true
    }
  ]
})
export class SzPasswordInputComponent implements ControlValueAccessor {
  readonly label: InputSignal<string | null> = input<string | null>(null);
  readonly placeholder: InputSignal<string> = input<string>('');
  readonly autocomplete: InputSignal<SzPasswordAutocomplete> = input<SzPasswordAutocomplete>('current-password');
  readonly helperText: InputSignal<string | null> = input<string | null>(null);
  readonly errorText: InputSignal<string | null> = input<string | null>(null);
  readonly inputId: InputSignal<string | null> = input<string | null>(null);

  readonly value: WritableSignal<string> = signal<string>('');
  readonly revealed: WritableSignal<boolean> = signal<boolean>(false);
  readonly disabledState: WritableSignal<boolean> = signal<boolean>(false);

  readonly type: Signal<'password' | 'text'> = computed(() => (this.revealed() ? 'text' : 'password'));
  readonly toggleAriaLabel: Signal<string> = computed(() => (this.revealed() ? 'Ukryj hasło' : 'Pokaż hasło'));

  private onChange: (value: string) => void = (): void => {};
  private onTouched: () => void = (): void => {};

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledState.set(isDisabled);
  }

  onInput(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    this.value.set(target.value);
    this.onChange(target.value);
  }

  onBlur(): void {
    this.onTouched();
  }

  toggleReveal(): void {
    this.revealed.update((v: boolean): boolean => !v);
  }
}
